import { Button, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import MuiInput from "@shared/ui/mui-input/MuiInput";
import { useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { deleteEmptyFieldsFromObject, groupBy } from "@shared/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { getToastErrorMessage } from "@shared/lib/errors"
import AlertDialog from "@shared/ui/alert-dialog/AlertDialog";
import { CarContext } from "./CarContext";
import { StyledEditCar } from "./EditCar.styled";
import { useLocation, useNavigate } from "react-router-dom";
import SettingsIcon from '@mui/icons-material/Settings';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import { useTheme } from "@emotion/react";
import { setCustomDataArray } from "@features/tickets/shared/utils/customData";
import { CarEdit, CarEditSchema } from "@models/car";
import { useGetAccountStructureQuery } from "@features/structures/api/accountsStructure.api";
import ReturnButton from "@shared/ui/navigation/ReturnButton";
import { editLastBread, getLastAvailableLink } from "@features/tickets/shared/utils/breadcrumbs";
import { useDeleteCarMutation, useSaveCarMutation } from "@features/tickets/api/cars.api";

function EditCar() {

  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: carStructure } = useGetAccountStructureQuery("רכב");

  const structureSortedByTitle = groupBy(carStructure, "account_title")
  const customFieldsExist = structureSortedByTitle?.["מותאם אישית"]?.find(structure => !!structure.show_form === true);

  const { car, setCar } = useContext(CarContext);
  const [saveCar] = useSaveCarMutation();
  const [deleteCar] = useDeleteCarMutation();


  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  const form = useForm<CarEdit>({
    resolver: zodResolver(CarEditSchema),
    defaultValues: {
      ...deleteEmptyFieldsFromObject(car)
    }
  });

  useEffect(() => {
    if (car) {
      form.reset(car);
    }
  }, [car]);

  useEffect(() => {
    if (location.state?.selectedAccountNumber !== undefined) {
      form.setValue("account_number", location.state.selectedAccountNumber, { shouldDirty: true, shouldValidate: true });
    }
  }, [location.state?.selectedAccountNumber]);


  const saveTicket = async () => {

    try {

      
      // const customDataObj = filterCustomKeys(form.getValues());
      // const customDataArray = returnArrayOfCustomData(customDataObj as FormData).filter(obj => obj.field_value !== "");
      const customDataArray = form.getValues("custom_data");
      setCustomDataArray(customDataArray);
      form.setValue("custom_data", customDataArray);

      form.handleSubmit(onSubmit)();

    } catch (error) {
      console.error("Form submission failed:", error);
    }

  }


  const onSubmit: SubmitHandler<CarEdit> = (data) => {

    const savePromise = async () => {
      const savedData = await saveCar({
        carId: car!.id,
        carToSave: data,
      }).unwrap();
      setCar(savedData);
      // console.log("on submit saved car", savedData);
      // console.log("on submit state", location.state);

      if (location.state?.breadcrumbData) {
        navigate(getLastAvailableLink(location.state.breadcrumbData), {
          replace: true,
          state: {
            ...location.state,
            document_account: {...savedData.customer, id: undefined, account_id: savedData.customer?.id},
            car: {...savedData, id: undefined, car_id: savedData.id},
            breadcrumbData: location.state.breadcrumbData.slice(0,-1)

          }
        });
      }
    }


    toast.promise(
      savePromise,
      {
        pending: 'מעדכן כרטיס...',
        success: 'הכרטיס עודכן בהצלחה',
        error: {
          render({ data }) {
            return getToastErrorMessage(data)
          }
        }
      }
    )
  };


  const onDelete = async () => {

    const deletePromise = async () => {
      if (car) {
        await deleteCar(car.id!).unwrap();
        window.history.back();
      }
    }

    toast.promise(
      deletePromise,
      {
        pending: 'מוחק כרטיס...',
        success: 'הכרטיס נמחק בהצלחה',
        error: {
          render({ data }) {
            return getToastErrorMessage(data)
          }
        }
      }
    )
  };

  function createState(){
    return {
        ...location.state,
    }
  }
  
  function createBreadcrumbData(state: any){

    const breadcrumbData = [
      {
        name: "כרטסות",
      },
      {
        name: "רכבים",
        link: "/cars",
      },
      {
        name: "עריכת כרטיס",
        link: `/cars/${car?.id}`
      },
    ];

    return [...(editLastBread(state?.breadcrumbData, `/cars/${car?.id}`, state) ?? breadcrumbData)];
  }

  function setStructure() {

    const currentState = createState();
    const breadcrumbData = createBreadcrumbData(currentState);

    navigate('/cars/structure/form', {
      replace: true,
      state: {
        ...currentState,
        breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה" }]
      }
    });
  }

  function browseCustomer() {

    const currentState = createState();
    const breadcrumbData = createBreadcrumbData(currentState);

    navigate('/customers', {
      replace: true,
      state: {
        ...currentState,
        customerSelectFor: "car-edit",
        breadcrumbData: [...breadcrumbData, { name: "בחירת לקוח" }]
      }
    });
  }

  return (
    <StyledEditCar>
      {!carStructure ? <Skeleton variant="rectangular" height={500} animation="wave" sx={{ bgcolor: theme.colors.section_color }} /> :
        <>

          <div className="form">
            <div><div className="left">
              {/* <button onClick={setStructure}>הגדרת שדות</button> */}
              <Tooltip title="הגדרת שדות">
                <IconButton onClick={setStructure} sx={{ color: theme.colors.main_color }}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </div></div>

            <div>
              <Typography variant="body1">פרטים:</Typography>
              <div className="fields">
                {structureSortedByTitle?.["פרטים"]?.map(field => {
                  if (!field.show_form) return null;

                  if (field.field_name === "account_number") {
                    return (
                      <MuiInput
                        key={field.field_name}
                        field={field}
                        variant='outlined'
                        useForm={form}
                        showRequired
                        blockNotEditable
                        adornmentIcon={
                          <Tooltip title="בחירת לקוח מרשימת הלקוחות" placement="top">
                            <IconButton onClick={browseCustomer} size="small" sx={{ color: theme.colors.main_color }}>
                              <AutoStoriesIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                      />
                    );
                  }

                  return <MuiInput key={field.field_name} field={field} variant='outlined' useForm={form} showRequired blockNotEditable />;
                })}
              </div>
            </div>

            <div>
              <Typography variant="body1">יצרן ודגם:</Typography>
              <div className="fields">
                {structureSortedByTitle?.["יצרן ודגם"]?.map(field => {
                  return field.show_form ? <MuiInput key={field.field_name} field={field} variant='outlined' useForm={form} showRequired blockNotEditable /> : null
                })}
              </div>
            </div>

            <div>
              <Typography variant="body1">הכנסות והוצאות:</Typography>
              <div className="fields">
                {structureSortedByTitle?.["הכנסות והוצאות"]?.map(field => {
                  return field.show_form ? <MuiInput key={field.field_name} field={field} variant='outlined' useForm={form} showRequired blockNotEditable /> : null
                })}
              </div>
            </div>

            <div>
              {customFieldsExist && (<Typography variant="body1">שדות מותאמים אישית:</Typography>)}
              <div className="fields">
                {structureSortedByTitle?.["מותאם אישית"]?.map(field => {
                  return field.show_form ? <MuiInput key={field.field_name} field={field} variant='outlined' useForm={form} showRequired blockNotEditable /> : null
                })}
              </div>
            </div>

          </div>

          <div className="buttons">
            <div className="right">
              <Button variant="contained" onClick={saveTicket} disabled={form.formState.isSubmitting} >שמירה</Button>
              {/* <Button variant="outlined" onClick={returnFunc}>חזור</Button> */}
              <ReturnButton />
              <Button variant="outlined" color="error" onClick={() => setDialogIsOpen(true)} >מחיקה</Button>
            </div>
            <div className="left">
            </div>
          </div>

        </>
      }
      <AlertDialog
        open={dialogIsOpen}
        handleClose={() => { setDialogIsOpen(false) }}
        handleConfirm={onDelete}
        title="אישור מחיקה"
        message="אתה בטוח שאתה רוצה למחוק כרטיס זה?"
        confirm="מחיקה"
      />
    </StyledEditCar>
  )
}


export default EditCar